import * as React from 'react'
import type { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import type { StudioAppSource } from './app-source'

export type AppSwitchCluster =
  | 'audio'
  | 'video'
  | 'marketing'
  | 'utilities'
  | 'entertainment'
  | '3d'
  | 'image'
  | 'crafting'
  | 'workspaces'
  | 'pinned'

export type AppSwitchCategory = 'ai' | 'more'

export type StudioLayoutLinkProps = {
  to: string
  reloadDocument?: boolean
  mask?: boolean
} & Omit<React.ComponentPropsWithRef<'a'>, 'href'>

export type StudioLayoutLinkComponent = React.ComponentType<StudioLayoutLinkProps>

/**
 * i18n adapter — the shared Studio chrome must not depend on any concrete i18n
 * library. Consumers inject a `t` function: Vite apps (studio, freemode,
 * lumo) back it with `react-i18next`, Next.js apps (web, freemode-landing-pages) with
 * `next-intl`.
 *
 * Keys are the fully-qualified dotted strings the chrome already uses
 * (e.g. `studio.clusters.image.label`). The package can't import the app-generated
 * translation key unions (package→app is forbidden), so the type enforces the
 * `studio.` root rather than a literal enum.
 */
export type StudioTranslationKey = `studio.${string}`

export type StudioTranslateFn = (
  key: StudioTranslationKey,
  options?: Record<string, unknown>,
) => string

/**
 * Routing adapter — lets the shared Studio chrome (navigation, top bar, processing
 * queue) work under either `react-router` (Vite apps: studio, freemode) or
 * `next/navigation` (Next.js apps: web, freemode-landing-pages). The react-router
 * defaults and the hooks that resolve them live in `./studio-config-router`, which
 * Next.js hosts never import; they inject their own implementations on `StudioConfig`.
 */
export interface MatchPathPattern {
  path: string
  end: boolean
}

export type MatchPathFn = (pattern: MatchPathPattern, pathname: string) => boolean

export type NavigateToFn = (
  path: string,
  options?: { mask?: string; state?: unknown; replace?: boolean; reloadDocument?: boolean },
) => void

/** One sidebar link: title, route, icon. The counterpart to a cluster folder. */
export interface SingularNavigationItem {
  title: string
  route: string
  icon: React.ReactNode
  /** For rows whose route is a prefix of every sibling's, so they don't all match at once. */
  exact?: boolean
  /**
   * Same as `AppSwitchBarItem.hardNavigation`: full-document load instead of SPA
   * routing. Needed when `route` is another origin (e.g. Freemode tools).
   */
  hardNavigation?: boolean
}

/** A catalog cluster folder inside a category section (Image, Video, …). */
export interface CategoryClusterEntry {
  kind: 'cluster'
  cluster: AppSwitchCluster
}

/** A single link inside a category section. */
export interface CategorySingularEntry extends SingularNavigationItem {
  kind: 'singular'
}

export type CategoryNavigationEntry = CategoryClusterEntry | CategorySingularEntry

/**
 * A bordered sidebar group with a heading. Children are catalog cluster folders
 * and/or singular links, in `entries` order.
 *
 * `category` folds extraToolGroups and unclustered catalog tools into this
 * section, and supplies the built-in heading when `title` is omitted.
 */
export interface CategoryNavigationSection {
  kind: 'category'
  title?: string
  category?: AppSwitchCategory
  entries: CategoryNavigationEntry[]
}

/** The Home row, plus any rows that belong beside it. */
export interface HomeNavigationSection {
  kind: 'home'
  /** Defaults to the main Studio home. */
  route?: string
  items?: SingularNavigationItem[]
}

/** The user's favorited tools. Surfaces with no catalog tools omit it. */
export interface PinnedNavigationSection {
  kind: 'pinned'
}

export type NavigationSectionConfig =
  | CategoryNavigationSection
  | HomeNavigationSection
  | PinnedNavigationSection

export function navigationSectionClusterIds(section: NavigationSectionConfig): AppSwitchCluster[] {
  if (section.kind !== 'category') {
    return []
  }
  return section.entries.flatMap(entry => (entry.kind === 'cluster' ? [entry.cluster] : []))
}

/**
 * A host-supplied navigation group that surfaces existing Studio tools (matched
 * by route) under a custom title and category. Unlike category `singular` entries
 * — which render arbitrary links — this group reuses the known
 * `AppSwitchBarItem`s for its routes (icons, titles, favorite behavior) and
 * dedupes them from their original clusters when enabled.
 *
 * The desktop shell uses this to render its "Local models" group from
 * desktop-owned routes, so shared Studio packages never hardcode local-model
 * support.
 */
export interface NavigationToolGroupConfig {
  id: string
  title: string
  category: AppSwitchCategory
  /** Routes of existing Studio tools to surface under this group. */
  toolRoutes: string[]
  /**
   * Hidden routes to surface under this group that have no public
   * `AppSwitchBarItem` (no protobuf `ToolType`, no category-page card). Unlike
   * `toolRoutes` these are rendered directly from the supplied title/route/icon,
   * so the group can include desktop-only local routes (e.g. Text to speech)
   * that aren't part of the shared tool catalog.
   */
  singularItems?: SingularNavigationItem[]
  /** Group icon, provided host-side (icons can't cross the desktop bridge). */
  icon: React.ReactNode
  /**
   * When `false`, the group stays visible but renders a message-only panel and
   * does not claim its tools from their original clusters. Defaults to `true`.
   */
  enabled?: boolean
  /** Message shown in the group's panel when `enabled` is `false`. */
  disabledMessage?: string
  /**
   * Where the group renders in the navigation. `'section'` (default) folds it
   * into the section matching its `category`. `'after-home'` lifts it to its
   * own row directly under Home — above pinned tools and section headings —
   * while keeping its grouped submenu (popover/sub-list) behavior.
   */
  placement?: 'section' | 'after-home'
}

/** The utility row pinned to the bottom of the sidebar. */
export type NavigationFooterItem =
  | { kind: 'api-platform' }
  | { kind: 'mcp'; href?: string }
  | { kind: 'characters'; route?: string }
  | { kind: 'dark-mode' }
  | { kind: 'language' }
  | { kind: 'menu' }

export type NavigationPromo = 'coins' | 'none' | 'unlimited'

export interface NavigationFooterProps {
  expanded: boolean
  onNavigate?: () => void
}

/** A Studio surface with its own branding and home. Values live in `STUDIO_WORKSPACES`. */
export type StudioWorkspaceId =
  | 'studio-ai'
  | 'marketing-studio'
  | 'freemode'
  | 'unlimited'
  | '3d'
  | 'platform'
  | 'desktop'
  | 'workflows'
  | 'lumo-canvas'

export interface NavigationConfig {
  Footer?: React.ComponentType<NavigationFooterProps>
  sections: NavigationSectionConfig[]
  /** Defaults to the full Studio footer. */
  footer?: NavigationFooterItem[]
  /** Sidebar upsell above the footer. Defaults to `'coins'`. */
  promo?: NavigationPromo
  visibleToolKeys?: Partial<Record<AppSwitchCluster, number[]>>
  extraToolGroups?: NavigationToolGroupConfig[]
}

export interface CompactNavigationMenuContentProps {
  onNavigate?: () => void
}

/**
 * A registered menu available from the compact Studio navigation on every route.
 * Its trigger sits in the side sheet's bottom row from `md` up; below `md` the
 * menu only opens through `defaultPathPattern`.
 */
export interface CompactNavigationMenuConfig {
  id: string
  label: string
  icon: React.ReactNode
  Content: React.ComponentType<CompactNavigationMenuContentProps>
  /** When matched, this menu is selected each time a new compact-navigation session opens. */
  defaultPathPattern?: MatchPathPattern
}

export interface ContextualNavigationItem {
  id: string
  label: string
  icon: React.ReactNode
  /** Omit until the destination has a real route. */
  route?: string
}

export interface ContextualNavigationSection {
  id: string
  items: ContextualNavigationItem[]
}

export interface ContextualNavigationConfig {
  id: string
  /** Route prefix that activates this navigation context. */
  path: string
  label: string
  sections: ContextualNavigationSection[]
}

/**
 * Whether the layout renders Studio's navigation. `'none'` is for a surface
 * that carries its own — the sidebar, its sheet and its toggle all go.
 */
export type StudioNavigationMode = 'sidebar' | 'none'

/** Host-specific replacements for shared layout links keyed by Studio chrome usage. */
export interface StudioRouteOverrides {
  /** Ellipsis menu “AI Models” target. Defaults to `ROUTES.MODELS.ROOT`. */
  modelsCatalog?: string
}

export interface StudioConfig {
  LinkComponent?: StudioLayoutLinkComponent
  /** Host-owned account settings dialog rendered alongside the shared layout. */
  accountSettingsDialog?: React.ReactNode
  navigationConfig?: NavigationConfig
  /**
   * Active workspace, set by `StudioWorkspaceProvider`. Tools that belong to one
   * workspace are only listed while it is active.
   */
  workspaceId?: StudioWorkspaceId
  compactNavigationMenus?: CompactNavigationMenuConfig[]
  contextualNavigation?: ContextualNavigationConfig[]
  /** Defaults to `'sidebar'`. */
  navigation?: StudioNavigationMode
  /** Extra actions rendered in Studio's top app bar by host apps. */
  topBarActions?: React.ReactNode
  /**
   * Branding logo rendered in the StudioLayout header. Pass `null` (or omit)
   * for layouts/tests that intentionally render no logo.
   */
  logo?: React.ReactNode
  /**
   * Replaces the whole logo slot, including the home link that wraps `logo`.
   * For branding that is itself interactive: a popover trigger cannot live
   * inside an anchor.
   */
  logoSlot?: React.ReactNode
  isNsfwPlatform?: boolean
  appSource?: StudioAppSource
  /** Only for surfaces `appSource` cannot express; Studio and Freemode derive theirs from it. */
  surface?: Surface
  /**
   * Canonical base URL for referral invite links (e.g. `https://studio.creativefabrica.com`).
   * Each app supplies its own so the shared referrals UI builds links for the right domain.
   */
  referralBaseUrl?: string
  /**
   * Consent gate for user uploads, consumed via `useRequestUploadConsent`.
   * Freemode injects a modal-backed gate; apps that omit it upload without one.
   */
  requestUploadConsent?: () => Promise<boolean>
  /**
   * Helmet title template applied to `Seo` page titles, e.g. `'%s - FreeMode.ai'`.
   * When omitted, defaults to the Studio branding (`'%s - idea.ai'`). Apps with their own
   * brand (e.g. Freemode) set this so their titles aren't branded as Studio.
   */
  seoTitleTemplate?: string
  /** Host overrides for shared chrome links (e.g. Freemode catalog vs characters). */
  routeOverrides?: StudioRouteOverrides
  /**
   * i18n adapter — required for the shared chrome to resolve its strings. There is
   * no default (the package is i18n-library agnostic); `useStudioTranslate` throws
   * when it isn't injected. Vite apps inject a `react-i18next`-backed function,
   * Next.js apps a `next-intl`-backed one.
   */
  t?: StudioTranslateFn
  /**
   * Routing adapter hooks. When omitted, the shared chrome falls back to
   * `react-router` (see the `useStudio*` resolver hooks in `./studio-config-router`).
   * Next.js apps inject `next/navigation`-backed implementations here.
   */
  useCurrentPathname?: () => string
  /** Visible route layered over the current pathname, for routers with masked navigation. */
  useCurrentMaskPathname?: () => string | undefined
  useCurrentLocationState?: () => unknown
  /**
   * Must navigate to the path it is given, without reapplying a URL locale.
   *
   * This one field backs both `useStudioNavigateTo` and
   * `useStudioNavigateToExact`, so an adapter that localizes would collapse the
   * distinction the locale layer depends on and silently restore the bug where
   * switching to English leaves the URL on the old prefix. Only the react-router
   * defaults differ; the Next.js adapters navigate exactly and serve both.
   */
  useNavigateTo?: () => NavigateToFn
  /**
   * Rewrites the URL's locale prefix in place, without navigating.
   *
   * Hosts whose router never sees the locale prefix inject this, and switching
   * language then costs nothing: no rematch, no loader, no remount. Hosts that
   * do route on the prefix leave it out and the locale layer falls back to a
   * `replace` navigation to the canonical path.
   */
  setUrlLocaleSegment?: (segment: string | undefined) => void
  matchPath?: MatchPathFn
}

export const StudioConfigContext = React.createContext<StudioConfig | undefined>(undefined)

export function StudioConfigProvider({
  config,
  children,
}: React.PropsWithChildren<{ config?: StudioConfig }>) {
  return <StudioConfigContext.Provider value={config}>{children}</StudioConfigContext.Provider>
}

export function useStudioConfig(): StudioConfig | undefined {
  return React.useContext(StudioConfigContext)
}

export function useStudioNavigationConfig(): NavigationConfig | undefined {
  return useStudioConfig()?.navigationConfig
}

/** `undefined` when the host routes on the locale prefix. See `setUrlLocaleSegment`. */
export function useStudioSetUrlLocaleSegment(): StudioConfig['setUrlLocaleSegment'] {
  return useStudioConfig()?.setUrlLocaleSegment
}

// --- Upload consent adapter -------------------------------------------------
// Defaults to allow, so apps without an injected gate (e.g. Studio) are unaffected.

const allowUpload: () => Promise<boolean> = () => Promise.resolve(true)

export function useRequestUploadConsent(): () => Promise<boolean> {
  return useStudioConfig()?.requestUploadConsent ?? allowUpload
}

// --- i18n adapter: resolver hook --------------------------------------------
// No fallback: the shared chrome is i18n-library agnostic, so a consumer must
// inject `t`. Throwing surfaces a missing provider immediately instead of
// rendering raw keys.

export function useStudioTranslate(required?: true): StudioTranslateFn
export function useStudioTranslate(required: false): StudioTranslateFn | undefined
export function useStudioTranslate(required: boolean = true): StudioTranslateFn | undefined {
  const t = useStudioConfig()?.t
  if (required && !t) {
    throw new Error(
      'useStudioTranslate: StudioConfig.t is required. ' +
        'Wrap with <StudioConfigProvider config={{ t }}> or pass a t stub in test-utils.',
    )
  }
  return t
}

// --- StudioLayoutContext: layout-level overrides ----------------------------

interface StudioLayoutContextValue {
  LinkComponent?: StudioLayoutLinkComponent
  accountSettingsDialog?: React.ReactNode
  compactNavigationMenus?: CompactNavigationMenuConfig[]
  contextualNavigation?: ContextualNavigationConfig[]
  navigationConfig?: NavigationConfig
  navigation?: StudioNavigationMode
  topBarActions?: React.ReactNode
  logo?: React.ReactNode
  logoSlot?: React.ReactNode
}

export const StudioLayoutContext = React.createContext<StudioLayoutContextValue>({})

export function useStudioLayoutLogo(): React.ReactNode | undefined {
  const layoutContext = React.useContext(StudioLayoutContext)
  const studioConfig = useStudioConfig()

  return layoutContext.logo ?? studioConfig?.logo
}

export function useStudioLayoutLogoSlot(): React.ReactNode | undefined {
  const layoutContext = React.useContext(StudioLayoutContext)
  const studioConfig = useStudioConfig()

  return layoutContext.logoSlot ?? studioConfig?.logoSlot
}
